import {useEffect, useRef, useState, useSyncExternalStore} from 'react'
import type {GenericContent} from '../../../../../../brand/lib/types/contentful'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import {Heading, Image, Text} from '@primer/react-brand/esm'
import {ArrowLeftIcon, ArrowRightIcon} from '@primer/octicons-react'
import {Label} from '../../_shared/Label/Label'

import Styles from './Gallery.module.css'

type Props = {
  content: GenericContent
  sectionIndex: number
}

// Module-scope so the references are stable across renders without useCallback
// (useSyncExternalStore would resubscribe if these changed identity).
function subscribeColumnCount(onChange: () => void) {
  const wideQuery = window.matchMedia('(min-width: 1280px)')
  const mediumQuery = window.matchMedia('(min-width: 804px)')
  wideQuery.addEventListener('change', onChange)
  mediumQuery.addEventListener('change', onChange)
  return () => {
    wideQuery.removeEventListener('change', onChange)
    mediumQuery.removeEventListener('change', onChange)
  }
}

function getColumnCountSnapshot(): number {
  if (window.matchMedia('(min-width: 1280px)').matches) return 3
  if (window.matchMedia('(min-width: 804px)').matches) return 2
  return 1
}

// `0` is the SSR/pre-hydration sentinel so pagination stays hidden until the
// client measures the viewport.
function getColumnCountServerSnapshot(): number {
  return 0
}

/**
 * SSR-safe hook returning the gallery column count for the current viewport
 * (3 at ≥1280px, 2 at ≥804px, else 1). Returns `0` on the server and before
 * hydration. Uses `useSyncExternalStore` over matchMedia so it only recomputes
 * when a breakpoint is crossed, not on every resize.
 */
function useColumnCount(): number {
  return useSyncExternalStore(subscribeColumnCount, getColumnCountSnapshot, getColumnCountServerSnapshot)
}

export function Gallery(props: Props) {
  const {content, sectionIndex} = props
  const {heading, label, media} = content.fields

  const title = heading
  const eyebrow = label?.fields.text

  // Gallery pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')

  // Column count is driven by viewport breakpoints. Media queries fire only when
  // a column-count breakpoint is crossed, unlike a ResizeObserver on
  // documentElement which reacts to every layout change. `0` is the SSR/
  // pre-hydration sentinel: pagination stays hidden until the client measures.
  const columnCount = useColumnCount()
  const isInitialized = columnCount !== 0
  const itemsPerPage = columnCount || 1

  // Reset to the first page whenever the column count changes (set state during
  // render rather than in an effect, per React 19 guidance).
  const [prevColumnCount, setPrevColumnCount] = useState(columnCount)
  if (prevColumnCount !== columnCount) {
    setPrevColumnCount(columnCount)
    setCurrentPage(1)
  }

  const totalPages =
    Array.isArray(media) && media.length > 0 && itemsPerPage > 0 ? Math.ceil(media.length / itemsPerPage) : 0

  const handlePrevious = () => {
    setDirection('backward')
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNext = () => {
    setDirection('forward')
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  // Animation
  const wrapper1Ref = useRef<HTMLDivElement>(null)
  const wrapper2Ref = useRef<HTMLDivElement>(null)

  const headingRef = useRef<HTMLDivElement>(null)
  const assetRefsArray = useRef<Array<HTMLDivElement | null>>([])

  useEffect(() => {
    const elements1: AnimationElement[] = []
    if (headingRef.current) elements1.push({element: headingRef.current})
    const cleanup1 = wrapper1Ref.current ? animateFadeIn(elements1, wrapper1Ref.current) : undefined

    const elements2: AnimationElement[] = []
    for (let i = 0; i < itemsPerPage; i++) {
      if (assetRefsArray.current[i]) {
        elements2.push({element: assetRefsArray.current[i]!, animationType: 'asset'})
      }
    }
    const cleanup2 = wrapper2Ref.current ? animateFadeIn(elements2, wrapper2Ref.current) : undefined

    return () => {
      cleanup1?.()
      cleanup2?.()
    }
  }, [itemsPerPage])

  return (
    <div className={Styles.Gallery}>
      <div className={Styles.Wrapper} ref={wrapper1Ref}>
        <div className={Styles.Cell}>
          {eyebrow && <Label className={Styles.Eyebrow} text={eyebrow} />}

          {title && (
            <Heading className={Styles.Title} data-heading="1" as={sectionIndex === 0 ? 'h2' : 'h3'} ref={headingRef}>
              {title}
            </Heading>
          )}
        </div>

        <div className={Styles.Cell}>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className={Styles.Pagination} style={{visibility: isInitialized ? 'visible' : 'hidden'}}>
              <button
                className={Styles.PaginationButton}
                aria-label={t('Previous page')}
                aria-disabled={currentPage === 1}
                onClick={handlePrevious}
              >
                <ArrowLeftIcon />
              </button>

              <Text className={Styles.PaginationLabel} size="200" variant="muted">
                {currentPage} / {totalPages}
              </Text>

              <button
                className={Styles.PaginationButton}
                aria-label={t('Next page')}
                aria-disabled={currentPage === totalPages}
                onClick={handleNext}
              >
                <ArrowRightIcon />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={Styles.Grid}
        data-direction={direction}
        data-items={media && media.length >= 3 ? 3 : 2}
        style={{visibility: isInitialized ? 'visible' : 'hidden'}}
        ref={wrapper2Ref}
      >
        {media?.map((item, itemIndex) => {
          const figure = item.fields
          const {asset} = figure || {}

          const startIndex = (currentPage - 1) * itemsPerPage
          const endIndex = startIndex + itemsPerPage
          const isVisible = itemIndex >= startIndex && itemIndex < endIndex

          return figure ? (
            <figure
              className={Styles.Figure}
              key={item.sys?.id || itemIndex}
              data-visible={isVisible}
              style={{'--delay': `${(itemIndex - startIndex) * 60}ms`}}
            >
              {asset?.fields.file.details?.image && (
                <div
                  ref={el => {
                    assetRefsArray.current[itemIndex] = el
                  }}
                  className={Styles.ImageWrapper}
                  data-animate="container"
                >
                  <div data-animate="item">
                    <Image
                      className={Styles.Image}
                      src={asset.fields.file.url}
                      width={figure.width || asset.fields.file.details?.image?.width || ''}
                      height={figure.height || asset.fields.file.details?.image?.height || ''}
                      alt={asset.fields.description || ''}
                      loading="lazy"
                    />
                  </div>
                  <div data-animate="cover" />
                </div>
              )}

              {figure.description && (
                <figcaption className={Styles.Caption}>
                  <Text size="300" variant="muted">
                    {figure.description}
                  </Text>
                </figcaption>
              )}
            </figure>
          ) : null
        })}
      </div>
    </div>
  )
}
